import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import type { PapiDeviation } from '@wix/da-papi-types';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import EditWithMuroIcon from '@wix/da-ds/pkg/Icons/16x16/Image';
import ReusePromptIcon from '@wix/da-ds/pkg/Icons/16x16/ReusePrompt';
import CreateVariationsIcon from '@wix/da-ds/pkg/Icons/16x16/CreateVariations';
import RemoveBackgroundIcon from '@wix/da-ds/pkg/Icons/16x16/RemoveBackground';
import UpscaleIcon from '@wix/da-ds/pkg/Icons/Expand';
import {
  BiData,
  BiContextProvidedValue,
  type EditWithMuroBiEvent,
} from '@wix/da-bi/pkg/events';
import { usePushMiniMuroModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushMuroModal';
import {
  useCopyPromptAction,
  useCreateVariationAction,
  useUpscaleAction,
  useDownloadAction,
  useRemoveBackgroundAction,
} from '../../../hooks';
import SubmitButton from '../../_partials/SubmitButton';
import ActionBarIconButton from '../ActionBarIconButton';
import MoreMenuButton from '../MoreMenuButton';
import ActionBarTextButton from '../ActionBarTextButton';
import { sanitizeHref, Url } from '@wix/da-url';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import VideoCreatorIcon from '@wix/da-ds/pkg/Icons/VideoCreator';

import s from './ActionsBar.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const ActionsBar: FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const hasVideoCreator = useFlag('video_creator');

  const upscaleAction = useUpscaleAction(deviation, 'prompt');
  const removeBackgroundAction = useRemoveBackgroundAction(deviation, 'prompt');
  const createVariationAction = useCreateVariationAction(deviation);
  const copyPromptAction = useCopyPromptAction(deviation);
  const downloadAction = useDownloadAction(deviation);

  const pushMuroModal = usePushMiniMuroModal(deviation);

  return (
    <div className={c(s['root'], className)}>
      <div className={s['left']}>
        <ActionBarTextButton
          icon={ReusePromptIcon}
          disabled={copyPromptAction.disabled}
          onClick={copyPromptAction.onClick}
          biData={copyPromptAction.biData}
        >
          {t('dreamup.modal.promptInfo.actions.copyPrompt.label')}
        </ActionBarTextButton>

        {!hasVideoCreator && (
          <ActionBarTextButton
            icon={CreateVariationsIcon}
            disabled={createVariationAction.disabled}
            onClick={createVariationAction.onClick}
            biData={createVariationAction.biData}
          >
            {t('dreamup.modal.promptInfo.actions.createVariations.label')}
          </ActionBarTextButton>
        )}

        <ActionBarTextButton
          icon={UpscaleIcon}
          onClick={upscaleAction.onClick}
          disabled={upscaleAction.disabled}
          biData={upscaleAction.biData}
        >
          {t('dreamup.modal.promptInfo.actions.upscale.label')}
        </ActionBarTextButton>

        <ActionBarTextButton
          icon={EditWithMuroIcon}
          onClick={pushMuroModal}
          biData={BiData<EditWithMuroBiEvent>({
            evid: 926,
            deviationid: deviation.deviationId,
            sectionname: BiContextProvidedValue,
          })}
        >
          {t('dreamup.modal.promptInfo.actions.draw.label')}
        </ActionBarTextButton>

        {hasVideoCreator && (
          <ActionBarTextButton
            icon={VideoCreatorIcon}
            href={Url.videoCreatorLink({
              deviation,
            })}
          >
            {t('dreamup.modal.promptInfo.actions.videoCreator.label')}
          </ActionBarTextButton>
        )}

        <ActionBarTextButton
          icon={RemoveBackgroundIcon}
          onClick={removeBackgroundAction.onClick}
          disabled={removeBackgroundAction.disabled}
          biData={removeBackgroundAction.biData}
        >
          {t('dreamup.modal.promptInfo.actions.removeBackground.label')}
        </ActionBarTextButton>
      </div>
      <div className={s['right']}>
        <div className={s['right-icon-buttons']}>
          <ActionBarIconButton
            icon={DownloadIcon}
            tooltipText={t('dreamup.modal.promptInfo.actions.download.label')}
            aria-label={t('dreamup.modal.promptInfo.actions.download.label')}
            href={sanitizeHref(downloadAction.href)}
            disabled={downloadAction.disabled}
            biData={downloadAction.biData}
          />
          <MoreMenuButton deviation={deviation} />
        </div>
        <SubmitButton deviation={deviation} />
      </div>
    </div>
  );
};

ActionsBar.displayName = 'ActionsBar';

export default ActionsBar;
